import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  ShieldCheck,
  Activity,
  Loader2,
  AlertCircle,
  Map,
  Navigation,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Pencil,
  Trash2,
  Building2,
  Hash
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import styles from './ViewAttendance.module.css';
import api from '../../../api/axios';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import AttendanceDrawer from './AttendanceDrawer';
import NotFound from '../../../pages/Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchAttendanceRecord = async () => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      const mockData = {
        id: id,
        employee: 'Michael Chen',
        employee_id: 1,
        employee_code: 'EMP-2024-001',
        project_site: 'Downtown Plaza',
        project_site_id: 1,
        date: '2024-04-30',
        punch_in_time: '09:00:24',
        punch_in_lat: '19.0760',
        punch_in_long: '72.8777',
        status: 'present',
        department: 'Operations',
        is_manual_override: false
      };
      setRecord(mockData);
      setLoading(false);
    }, 800);
  };

  useEffect(() => {
    fetchAttendanceRecord();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Attendance?',
      text: `Are you sure you want to delete this record for ${record.employee}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete record'
    }).then((result) => {
      if (result.isConfirmed) {
        toast.success('Attendance record deleted');
        navigate('/employees/attendance');
      }
    });
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'present': return styles['status-present'];
      case 'half_day': return styles['status-halfday'];
      case 'absent': return styles['status-absent'];
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };

  if (loading) {
    return (
      <div className={styles['view-attendance-layout']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Retrieving Attendance Metrics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-attendance-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Attendance', path: '/employees/attendance' },
            { label: 'Details', path: `/employees/attendance/view/${id}` }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Attendance Details</h1>
              <p>Granular logs for punch times, geolocation, and OT credits.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/attendance')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Pencil size={18} />
                <span>Edit Record</span>
              </button>
              <button className={styles['btn-danger']} onClick={handleDelete}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          <div className={styles['attendance-hero-card']}>
            <div className={styles['hero-content']}>
              <div className={styles['hero-left']}>
                <div className={styles['employee-avatar']}>{record.employee.charAt(0)}</div>
                <div className={styles['employee-info']}>
                  <div className={styles['name-status-row']}>
                    <h2>{record.employee}</h2>
                    <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                      {record.status.toUpperCase()}
                    </span>
                  </div>
                  <div className={styles['meta-row']}>
                    <div className={styles['meta-item']}>
                      <Hash size={14} /> <span>{record.employee_code}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Building2 size={14} /> <span>{record.department}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Calendar size={14} /> <span>{formatDate(record.date)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['hero-right']}>
                 <div className={styles['site-location-box']}>
                    <label>Assigned Site</label>
                    <div className={styles['site-value']}>
                       <MapPin size={18} />
                       <span>{record.project_site}</span>
                    </div>
                 </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-grid']}>
             <div className={styles['metric-card']}>
                <div className={`${styles['metric-icon-box']} ${styles['blue']}`}><Clock size={20} /></div>
                <div className={styles['metric-info']}>
                   <label>Punch In Time</label>
                   <h3>{record.punch_in_time}</h3>
                </div>
             </div>

          </div>

          <div className={styles['details-grid']}>
             <div className={styles['main-column']}>
                <div className={styles['details-card']}>
                   <div className={styles['card-header']}>
                      <Navigation size={20} />
                      <h3>Geolocation Logs</h3>
                   </div>
                   <div className={styles['geo-logs']}>
                      <div className={styles['geo-item']}>
                         <div className={styles['geo-indicator-in']}></div>
                         <div className={styles['geo-content']}>
                            <label>Punch-In Coordinates</label>
                            <div className={styles['coordinates-row']}>
                               <span>Lat: <strong>{record.punch_in_lat}</strong></span>
                               <span>Long: <strong>{record.punch_in_long}</strong></span>
                               <a href={`https://www.google.com/maps?q=${record.punch_in_lat},${record.punch_in_long}`} target="_blank" rel="noreferrer" className={styles['map-link']}>View on Map</a>
                            </div>
                         </div>
                      </div>
                   </div>
                </div>

                <div className={styles['details-card']}>
                   <div className={styles['card-header']}>
                      <Activity size={20} />
                      <h3>Compliance & Verification</h3>
                   </div>
                   <div className={styles['compliance-list']}>

                      <div className={styles['compliance-item']}>
                         <div className={styles['c-left']}>
                            {record.is_manual_override ? <AlertCircle size={18} color="#f59e0b" /> : <CheckCircle2 size={18} color="#10b981" />}
                            <span>Entry Method</span>
                         </div>
                         <span className={styles['c-value']}>{record.is_manual_override ? 'Manual Override' : 'System Auto-Capture'}</span>
                      </div>
                   </div>
                </div>
             </div>

             <div className={styles['side-column']}>
                <div className={styles['standard-card']}>
                   <div className={styles['card-header']}>
                      <Map size={20} />
                      <h3>Site Proximity</h3>
                   </div>
                   <div className={styles['site-proximity-viz']}>
                      {/* This would normally be a map. For now, a stylized placeholder */}
                      <div className={styles['proximity-placeholder']}>
                         <div className={styles['radar-circle']}></div>
                         <MapPin size={24} className={styles['pin-pulse']} />
                         <span className={styles['proximity-text']}>Within Site Perimeter</span>
                      </div>
                      <div className={styles['proximity-stats']}>
                         <div className={styles['p-stat']}>
                            <label>Accuracy</label>
                            <span>± 10 meters</span>
                         </div>
                         <div className={styles['p-stat']}>
                            <label>Method</label>
                            <span>GPS / WiFi</span>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>

          <Footer />
        </main>
      </div>

      <AttendanceDrawer 
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
           setIsDrawerOpen(false);
           fetchAttendanceRecord();
        }}
        record={record}
      />
    </div>
  );
};

export default ViewAttendance;
